import { useEffect } from 'react';

// F1-F6 are the courtroom hotkeys. F1, F5, F6 collide with browser defaults
// (Help, Reload, Address bar) so handlers preventDefault when they fire.
// 03-ui-design-language.md notes the override; 07-courtroom-protocol.md
// reminds the operator not to hit Ctrl+R out of muscle memory mid-hearing.

type BaseHotkey = 'F1' | 'F2' | 'F3' | 'F4' | 'F5' | 'F6' | 'F7' | 'F8' | 'F9' | 'F10';
// P2 Section 1: Shift+F1..F4 trigger the DEEP variants of the primary actions.
type ShiftHotkey = 'Shift+F1' | 'Shift+F2' | 'Shift+F3' | 'Shift+F4';
export type Hotkey = BaseHotkey | ShiftHotkey;

export type HotkeyMap = Partial<Record<Hotkey, (event: KeyboardEvent) => void>>;

interface Options {
  enabled?: boolean;
  /** When true, hotkeys also fire while focus is in inputs/textareas. Default false. */
  fireInsideInputs?: boolean;
}

const ALL_BASE_HOTKEYS: ReadonlyArray<BaseHotkey> = [
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
];
// Only F1-F4 have a Shift variant (the four primary actions -> deep mode).
const SHIFTABLE: ReadonlyArray<BaseHotkey> = ['F1', 'F2', 'F3', 'F4'];

function isBaseHotkey(key: string): key is BaseHotkey {
  return (ALL_BASE_HOTKEYS as readonly string[]).includes(key);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  return target.isContentEditable;
}

/**
 * Subscribe to function-key hotkeys at the window level. The handler always
 * runs preventDefault when the key is bound, so F1/F5/F6 do not trigger
 * browser help / reload / address-bar focus.
 */
export function useHotkeys(map: HotkeyMap, options: Options = {}): void {
  const { enabled = true, fireInsideInputs = false } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    function onKeyDown(event: KeyboardEvent): void {
      // Ctrl/Alt/Meta with an F-key are OS/browser-reserved (e.g. Cmd+F4 closes
      // a window); never intercept those. Shift IS allowed for F1-F4 (deep mode).
      if (event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }
      if (!fireInsideInputs && isEditableTarget(event.target)) {
        return;
      }
      if (!isBaseHotkey(event.key)) {
        return;
      }
      // Resolve to a Shift+Fn binding when Shift is held and the key supports it;
      // otherwise the plain Fn binding. A bare Shift on F5-F10 is ignored.
      let lookup: Hotkey;
      if (event.shiftKey) {
        if (!(SHIFTABLE as readonly string[]).includes(event.key)) {
          return;
        }
        lookup = `Shift+${event.key}` as Hotkey;
      } else {
        lookup = event.key as Hotkey;
      }
      const handler = map[lookup];
      if (!handler) {
        return;
      }
      event.preventDefault();
      handler(event);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [map, enabled, fireInsideInputs]);
}
